<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import CloseIcon from '@/assets/icons/CloseIcon.vue';
import CompetenceIcon from '@/assets/icons/CompetenceIcon.vue';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import CompetenceDisplayList from '@/core/competences/CompetenceDisplayList.vue';
import { toSelectedCompetenceDisplayItems } from '@/core/competences/competenceDisplay.utils';
import CompetenceSelectionPopover from '@/core/selects/CompetenceSelectionPopover.vue';
import { EObservationTone, type ISelectedCompetence } from '@/interfaces';
import { computed, ref, useTemplateRef } from 'vue';

const emit = defineEmits<{
  'add:competence': [competenceId: number];
  'remove:competence': [competenceId: number];
  'update:competences': [competenceIds: number[]];
}>();

const props = withDefaults(
  defineProps<{
    allSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
    competenceDomains: Components.Schemas.CompetenceDomainDto[];
    /** Batch picker edits and emit `update:competences` once when the popover closes. */
    commitOnClose?: boolean;
    disableClickOutside?: boolean;
    disabled?: boolean;
    inlineEmpty?: boolean;
    mobileDrawer?: boolean;
    readOnly?: boolean;
    schoolSubjectId: number | null;
    selectedCompetences: ISelectedCompetence[];
    surface?: 'primary' | 'secondary';
    tone?: EObservationTone;
  }>(),
  {
    commitOnClose: false,
    disableClickOutside: false,
    disabled: false,
    inlineEmpty: false,
    mobileDrawer: false,
    readOnly: false,
    surface: 'primary',
    tone: EObservationTone.Default,
  },
);

const { getSchoolSubjectColor } = useSchoolSubject();
const triggerRef = useTemplateRef('triggerRef');
const popoverRef = useTemplateRef('popoverRef');
const popoverOpen = ref(false);

const lockedSchoolSubjectId = computed(
  () => props.schoolSubjectId ?? props.selectedCompetences.at(0)?.domain.schoolSubjectId ?? null,
);

const availableCompetenceDomains = computed(() => {
  if (lockedSchoolSubjectId.value == null) return props.competenceDomains;
  return props.competenceDomains.filter((domain) => domain.schoolSubjectId === lockedSchoolSubjectId.value);
});

const selectedCompetenceItems = computed(() =>
  toSelectedCompetenceDisplayItems(props.selectedCompetences, getSchoolSubjectColor),
);

const togglePopover = (): void => {
  if (props.disabled || props.readOnly) return;
  if (popoverOpen.value) {
    popoverRef.value?.hide();
    return;
  }
  popoverOpen.value = true;
  popoverRef.value?.show();
};

const handlePopoverHide = (): void => {
  popoverOpen.value = false;
};

const hide = (): void => {
  if (!popoverOpen.value) return;
  popoverRef.value?.hide();
};

const addCompetence = (competenceId: number): void => {
  if (!props.disabled && !props.readOnly) emit('add:competence', competenceId);
};

const removeCompetence = (competenceId: number): void => {
  if (!props.disabled && !props.readOnly) emit('remove:competence', competenceId);
};

const updateCompetences = (competenceIds: number[]): void => {
  if (!props.disabled) emit('update:competences', competenceIds);
};

defineExpose({ hide, isOpen: computed(() => popoverOpen.value) });
</script>

<template>
  <div
    class="flex flex-col items-start"
    :class="props.inlineEmpty && !props.selectedCompetences.length ? 'w-auto gap-0' : 'w-full gap-md'"
  >
    <CompetenceDisplayList
      v-if="props.selectedCompetences.length"
      :expandable="false"
      :items="selectedCompetenceItems"
      separator-inset="md"
      :surface="props.surface"
    >
      <template v-if="!props.readOnly" #trailing="{ item }">
        <button
          type="button"
          class="flex size-8 shrink-0 items-center justify-center rounded-button state-effects"
          :class="props.tone === EObservationTone.Note ? 'text-note-accent note-state-effects' : 'text-secondary'"
          :disabled="props.disabled"
          :aria-label="$t('competence.remove')"
          @click="removeCompetence(item.id)"
        >
          <CloseIcon class="size-icon-sm" />
        </button>
      </template>
    </CompetenceDisplayList>

    <button
      v-if="!props.readOnly"
      ref="triggerRef"
      type="button"
      class="chip border border-dashed bg-transparent"
      :class="
        props.tone === EObservationTone.Note
          ? 'border-note text-note-accent note-state-effects'
          : 'border-quaternary text-secondary'
      "
      :disabled="props.disabled"
      aria-haspopup="dialog"
      :aria-expanded="popoverOpen"
      :aria-label="$t('competence.select')"
      @click="togglePopover"
    >
      <CompetenceIcon class="size-3 shrink-0" />
      <span>{{ $t('note.linkCompetence') }}</span>
    </button>

    <CompetenceSelectionPopover
      v-if="!props.readOnly"
      ref="popoverRef"
      :anchor-element="triggerRef"
      :available-competence-domains="availableCompetenceDomains"
      :all-school-subjects="props.allSchoolSubjects"
      :commit-on-close="props.commitOnClose"
      :disable-click-outside="props.disableClickOutside"
      :disabled="props.disabled"
      :selected-competences="props.selectedCompetences.map(({ competence }) => competence)"
      :hide-school-subjects="lockedSchoolSubjectId != null"
      :mobile-drawer="props.mobileDrawer"
      @add:competence="addCompetence"
      @remove:competence="removeCompetence"
      @update:competences="updateCompetences"
      @hide="handlePopoverHide"
    />
  </div>
</template>
